import React from 'react';

/**
 * Focuses the roster tile of `focusUserId` once, when that tile mounts
 * (SEL-35). `useSellerLogin` resolves the ID: the previously picked seller if
 * they are still listed, otherwise the first one. Returns a ref factory: bind
 * `tileRef(user.id)` to each tile.
 *
 * Applied from each tile's own ref callback rather than an effect that
 * queries the roster afterwards: `ListBox` builds its collection in a
 * follow-up render, so a tile's DOM node does not exist yet on the render
 * where the roster's own effects run. Once per mount, so a later render (a
 * refetched roster) never pulls focus back from a tile the seller moved to.
 */
export const useRosterFocus = (
    focusUserId: string | null,
): ((userId: string) => React.RefCallback<HTMLDivElement>) => {
    const hasFocusedRef = React.useRef(false);

    return React.useCallback(
        (userId: string) => (node: HTMLDivElement | null) => {
            if (node && !hasFocusedRef.current && userId === focusUserId) {
                node.focus();
                hasFocusedRef.current = true;
            }
        },
        [focusUserId],
    );
};
